// AuthContext.js
import React, { createContext, useContext, useEffect, useRef } from 'react';
import AppContext from './Context';
import { getWorkspaceMode } from 'helpers/workspace';
import axiosInstance from 'axiosInstance';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const {
    userData,
    setUserData,
    userDispatch,
  } = useContext(AppContext);
  const contextHydratedRef = useRef(false);

  const login = (user) => {
    setUserData('userId', user.userId);
    setUserData('userName', user.userName);
    setUserData('userEmail', user.userEmail);
    setUserData('user', user.user || null);
    setUserData('remember', user.remember);
    setUserData('integrations', user.integrations || {});
    setUserData('providers', user.providers || {});
    setUserData('permissions', user.permissions || []);
    setUserData('company', user.company || null);
    setUserData('companies', user.companies || []);
    setUserData('profile', user.profile || null);
    setUserData('membership', user.membership || null);
    setUserData('activeIntegration', user.activeIntegration || null);
    setUserData('workspace', user.workspace || { mode: 'gestion', label: 'Gestion', partner_code: '', partner_label: '' });
    userDispatch({ type: 'REFRESH' });
  };

  const refresh = (user) => {
    if(user.userId != undefined) setUserData('userId', user.userId);
    if(user.userName != undefined) setUserData('userName', user.userName);
    if(user.userEmail != undefined) setUserData('userEmail', user.userEmail);
    if(user.user != undefined) setUserData('user', user.user);
    if(user.remember != undefined) setUserData('remember', user.remember);
    if(user.integrations != undefined) setUserData('integrations', user.integrations);
    if(user.providers != undefined) setUserData('providers', user.providers);
    if(user.permissions != undefined) setUserData('permissions', user.permissions);
    if(user.company != undefined) setUserData('company', user.company);
    if(user.companies != undefined) setUserData('companies', user.companies);
    if(user.profile != undefined) setUserData('profile', user.profile);
    if(user.membership != undefined) setUserData('membership', user.membership);
    if(user.activeIntegration != undefined) setUserData('activeIntegration', user.activeIntegration);
    if(user.workspace != undefined) setUserData('workspace', user.workspace);
    userDispatch({ type: 'REFRESH' });
  };

  const lock = () => {
    // Clear the authentication token and user data
    // Remove the token from local storage or cookies
    // Update the user state to null
    setUserData('userId', -1);
  };

  const logout = async () => {
    // Call backend to invalidate the Django session
    try {
      await axiosInstance.post('login/logout');
    } catch (err) {
      // Proceed with local cleanup even if the server call fails
    }
    // Clear local authentication state
    setUserData('userId', -1);
    setUserData('userName', "");
    setUserData('userEmail', "");
    setUserData('user', null);
    setUserData('remember', false);
    setUserData('integrations', {});
    setUserData('providers', {});
    setUserData('permissions', []);
    setUserData('company', null);
    setUserData('companies', []);
    setUserData('profile', null);
    setUserData('membership', null);
    setUserData('activeIntegration', null);
    setUserData('workspace', { mode: 'gestion', label: 'Gestion', partner_code: '', partner_label: '' });
  };

  //Has the ID cleared but the email and name remains, so it can login again with the password
  const isLocked = () => {
    return (userData != null && userData != undefined) &&
    (userData.userId == undefined || userData.userId == -1 ) &&
    (userData.userName != undefined && userData.userName != "" ) &&
    (userData.userEmail != undefined && userData.userEmail != "" )
  }

  //Has the ID set
  const isAuthenticated = () => {
    return (userData != null && userData != undefined && userData.userId != undefined && userData.userId != -1)
  }

  //Has the ID set
  const hasIntegration = (integration) => {
    return isAuthenticated() && (
      userData.integrations != undefined && userData.integrations != null &&
      userData.integrations[integration] != undefined && userData.integrations[integration] != null &&
      userData.integrations[integration]
    )
  }

  const hasPermission = permissionCode => {
    if (isSuperAdmin()) {
      return true;
    }
    return isAuthenticated() &&
      userData.permissions != undefined &&
      userData.permissions != null &&
      Array.isArray(userData.permissions) &&
      userData.permissions.includes(permissionCode);
  }

  const hasAnyPermission = permissionCodes => {
    if (isSuperAdmin()) {
      return true;
    }
    return Array.isArray(permissionCodes) && permissionCodes.some(hasPermission);
  }

  const isSuperAdmin = () => {
    return isAuthenticated() &&
      userData.user != null &&
      userData.user.is_super_admin === true;
  }

  const getCurrentWorkspaceMode = () => getWorkspaceMode(userData);

  const switchContext = async ({ companyId, integrationId } = {}) => {
    const response = await axiosInstance.post('login/switch-context', {
      company_id: companyId,
      integration_id: integrationId
    });
    const auth = response.data.auth || {};
    refresh({
      integrations: response.data.integrations || {},
      providers: response.data.providers || {},
      permissions: auth.permissions || [],
      company: auth.company || null,
      companies: auth.companies || [],
      profile: auth.profile || null,
      membership: auth.membership || null,
      activeIntegration: auth.active_integration || response.data.active_integration || null,
      workspace: auth.workspace || null
    });
    return response.data;
  };

  useEffect(() => {
    if (!isAuthenticated()) {
      contextHydratedRef.current = false;
    }
  }, [userData.userId]);

  useEffect(() => {
    const shouldHydrateContext =
      isAuthenticated() &&
      !contextHydratedRef.current;

    if (!shouldHydrateContext) {
      return undefined;
    }

    contextHydratedRef.current = true;
    let cancelled = false;

    axiosInstance
      .get('login/me')
      .then(response => {
        if (cancelled) {
          return;
        }
        const auth = response.data.auth || {};
        refresh({
          integrations: response.data.integrations || {},
          providers: response.data.providers || {},
          permissions: auth.permissions || [],
          company: auth.company || null,
          companies: auth.companies || [],
          profile: auth.profile || null,
          membership: auth.membership || null,
          activeIntegration:
            auth.active_integration || response.data.active_integration || null,
          workspace: auth.workspace || null
        });
      })
      .catch(() => {
        contextHydratedRef.current = false;
      });

    return () => {
      cancelled = true;
    };
  }, [userData.userId]);

  return (
    <AuthContext.Provider value={{ userData, login, refresh, lock, logout, isLocked, isAuthenticated, hasIntegration, hasPermission, hasAnyPermission, isSuperAdmin, getCurrentWorkspaceMode, switchContext }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
